import {
  afterNextRender,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  NgZone,
  OnDestroy,
  untracked,
} from "@angular/core";

/** Sub-pixel slack so fractional layout widths can't flip a level back and forth. */
const FIT_TOLERANCE_PX = 0.5;

/**
 * The level a row should be at, given how wide it is and how wide each level was last measured to
 * need. Steps back down through every level whose remembered need now fits (so a big resize
 * doesn't have to re-render each level to find its way back), otherwise steps up one level if the
 * current one overflows. Returns `current` when nothing should change.
 */
export function nextFitLevel(
  current: number,
  maxLevel: number,
  availableWidth: number,
  neededWidths: ReadonlyArray<number | undefined>,
): number {
  let level = Math.min(current, maxLevel);
  while (level > 0 && (neededWidths[level - 1] ?? Infinity) <= availableWidth) {
    level--;
  }
  if (level !== current) {
    return level;
  }
  const needed = neededWidths[level];
  if (level < maxLevel && needed !== undefined && needed > availableWidth + FIT_TOLERANCE_PX) {
    return level + 1;
  }
  return level;
}

/**
 * Steps a single-line row through progressively more compact layouts until its content fits,
 * instead of letting it wrap at a hard-coded breakpoint that only holds for one set of content
 * (month name, amounts, currency, font).
 *
 * `fitLevel` 0 is the roomiest layout and each higher level is one more compaction step, up to
 * `appFitLevel` - the last level, which must be a layout that can't overflow sideways (e.g. one
 * that stacks or wraps). What each level looks like is up to the host's own bindings.
 *
 * Below the last level the host must lay its direct children out on one non-wrapping line with
 * `flex-shrink: 0`, so their summed widths plus the host's `column-gap` are exactly what that level
 * needs (children's margins aren't counted - an `ml-auto` spacer absorbs free space, not content).
 * That need is compared with the host's content width: overflowing steps up a level, and the need
 * is remembered so the row can step back down as soon as the host grows past it. A ResizeObserver
 * on the host and its children re-runs this whenever the available room or the content changes.
 */
@Directive({
  selector: "[appFitLevel]",
  standalone: true,
})
export class FitLevelDirective implements OnDestroy {
  /** The last (most compact) level. */
  readonly maxLevel = input.required<number>({ alias: "appFitLevel" });
  /** While true the row is pinned to level 0 and nothing is measured. */
  readonly disabled = input(false, { alias: "fitLevelDisabled" });
  /**
   * Any value that changes when the row's content does (labels, amounts). A change forgets the
   * remembered widths and re-fits from level 0 - otherwise content that got *narrower* would stay
   * compacted, since a remembered width is only ever corrected by re-rendering that level.
   */
  readonly content = input<unknown>(undefined, { alias: "fitLevelContent" });
  readonly fitLevel = model(0);

  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly neededWidths: number[] = [];
  private readonly observedChildren = new Set<Element>();
  // ResizeObserver callbacks aren't zone-patched in this app; re-enter the zone so the level
  // change is picked up by change detection.
  private readonly resizeObserver = new ResizeObserver(() => this.ngZone.run(() => this.fit()));

  constructor() {
    effect(
      () => {
        const disabled = this.disabled();
        this.maxLevel();
        this.content();
        untracked(() => {
          this.neededWidths.length = 0;
          this.fitLevel.set(0);
          if (disabled) {
            this.stopObserving();
            return;
          }
          this.resizeObserver.observe(this.host);
          this.scheduleFit();
        });
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.stopObserving();
  }

  private fit(): void {
    if (this.disabled()) {
      return;
    }
    this.observeChildren();
    const current = this.fitLevel();
    const maxLevel = this.maxLevel();
    if (current < maxLevel) {
      this.neededWidths[current] = this.measureNeededWidth();
    }
    const next = nextFitLevel(current, maxLevel, this.measureAvailableWidth(), this.neededWidths);
    if (next !== current) {
      this.fitLevel.set(next);
      // Measure the new level once it has rendered - it may still overflow, or (stepping down on
      // a remembered width that's since gone stale) overflow again and need to step back up.
      this.scheduleFit();
    }
  }

  private scheduleFit(): void {
    afterNextRender(() => this.fit(), { injector: this.injector });
  }

  private measureAvailableWidth(): number {
    const style = getComputedStyle(this.host);
    return (
      this.host.getBoundingClientRect().width -
      parseFloat(style.paddingLeft || "0") -
      parseFloat(style.paddingRight || "0") -
      parseFloat(style.borderLeftWidth || "0") -
      parseFloat(style.borderRightWidth || "0")
    );
  }

  private measureNeededWidth(): number {
    const rendered = this.renderedChildren();
    const gap = parseFloat(getComputedStyle(this.host).columnGap) || 0;
    const childrenWidth = rendered.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0);
    return childrenWidth + gap * Math.max(rendered.length - 1, 0);
  }

  /** Children that take part in layout (skips `display: none` ones, which also take no gap). */
  private renderedChildren(): Element[] {
    return Array.from(this.host.children).filter((el) => el.getClientRects().length > 0);
  }

  private observeChildren(): void {
    const current = new Set(Array.from(this.host.children));
    for (const el of this.observedChildren) {
      if (!current.has(el)) {
        this.resizeObserver.unobserve(el);
        this.observedChildren.delete(el);
      }
    }
    for (const el of current) {
      if (!this.observedChildren.has(el)) {
        this.resizeObserver.observe(el);
        this.observedChildren.add(el);
      }
    }
  }

  private stopObserving(): void {
    this.resizeObserver.disconnect();
    this.observedChildren.clear();
  }
}
